<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, reactive, computed, onMounted } from 'vue';
import nexceoReportsAPI from 'dashboard/api/nexceo/reports';
import nexceoProjectsAPI from 'dashboard/api/nexceo/projects';

// ── State ────────────────────────────────────────────────────────────────────

const isLoading = ref(false);
const loadError = ref(null);
const metrics = ref(null);
const snapshots = ref([]);
const projects = ref([]);
const generating = ref(false);
const testing = ref(false);
const actionMsg = ref(null);

const filters = reactive({
  range: 'today',
  start_date: '',
  end_date: '',
  project_id: '',
});

// ── Computed ─────────────────────────────────────────────────────────────────

const ov = computed(
  () => metrics.value?.overview || metrics.value?.metrics?.overview || {}
);
const projectMetrics = computed(() => {
  const p = metrics.value?.projects || metrics.value?.metrics?.projects || [];
  return [...p].sort((a, b) => (b.new_leads || 0) - (a.new_leads || 0));
});
const sourceMetrics = computed(
  () => metrics.value?.sources || metrics.value?.metrics?.sources || {}
);
const funnelMetrics = computed(
  () => metrics.value?.funnel || metrics.value?.metrics?.funnel || {}
);
const agentMetrics = computed(
  () => metrics.value?.agents || metrics.value?.metrics?.agents || []
);
const aiMetrics = computed(
  () => metrics.value?.ai || metrics.value?.metrics?.ai || {}
);
const ndMetrics = computed(
  () =>
    metrics.value?.notifications || metrics.value?.metrics?.notifications || {}
);

const funnelTotal = computed(() =>
  Object.values(funnelMetrics.value).reduce((s, v) => s + (v || 0), 0)
);

// AI tracking_available: false means the ai_logs table exists but is empty —
// show "Not tracked yet" instead of misleading 0/0.
const aiTrackingAvailable = computed(() => {
  const ai = aiMetrics.value;
  // If the field is absent (older backend), default to true so we show numbers.
  if (!('tracking_available' in ai)) return true;
  return ai.tracking_available;
});

// ── Date helpers ─────────────────────────────────────────────────────────────

// Use LOCAL calendar date, not UTC date (toISOString gives UTC which is wrong
// for IST users: at 00:30 IST the UTC date is still yesterday).
function localDateStr(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dateRange(range) {
  const now = new Date();
  const today = localDateStr(now);
  const yest = localDateStr(new Date(now.getTime() - 86400000));
  const w7 = localDateStr(new Date(now.getTime() - 7 * 86400000));
  const m30 = localDateStr(new Date(now.getTime() - 30 * 86400000));
  switch (range) {
    case 'yesterday':
      return { start_date: `${yest}T00:00:00`, end_date: `${yest}T23:59:59` };
    case 'last7':
      return { start_date: `${w7}T00:00:00`, end_date: `${today}T23:59:59` };
    case 'last30':
      return { start_date: `${m30}T00:00:00`, end_date: `${today}T23:59:59` };
    case 'custom':
      return { start_date: filters.start_date, end_date: filters.end_date };
    default:
      return { start_date: `${today}T00:00:00`, end_date: `${today}T23:59:59` };
  }
}

function funnelPct(count) {
  return funnelTotal.value > 0
    ? Math.round((count / funnelTotal.value) * 100)
    : 0;
}

// ── Data loading ─────────────────────────────────────────────────────────────

async function loadMetrics() {
  isLoading.value = true;
  loadError.value = null;
  try {
    const params = {
      ...dateRange(filters.range),
      project_id: filters.project_id || undefined,
    };
    const { data } = await nexceoReportsAPI.summary(params);
    metrics.value = data.metrics || data;
  } catch {
    loadError.value = 'Failed to load metrics. Please retry.';
  } finally {
    isLoading.value = false;
  }
}

async function loadSnapshots() {
  try {
    const { data } = await nexceoReportsAPI.snapshots({ limit: 10 });
    snapshots.value = data.snapshots || [];
  } catch {
    /* non-fatal */
  }
}

async function loadProjects() {
  try {
    const { data } = await nexceoProjectsAPI.list();
    projects.value = data.projects || [];
  } catch {
    /* non-fatal */
  }
}

// ── Actions ─────────────────────────────────────────────────────────────────

function clearActionMsg() {
  setTimeout(() => {
    actionMsg.value = null;
  }, 8000);
}

async function generateReport() {
  generating.value = true;
  actionMsg.value = null;
  try {
    const { data } = await nexceoReportsAPI.generateDaily({ deliver: false });
    actionMsg.value = {
      success: true,
      text: `Report generated: snapshot #${data.snapshot?.id} (${data.snapshot?.date_label}) — status: ${data.snapshot?.status}`,
    };
    await loadSnapshots();
  } catch (e) {
    actionMsg.value = {
      success: false,
      text: e?.response?.data?.error || 'Generation failed.',
    };
  } finally {
    generating.value = false;
    clearActionMsg();
  }
}

async function testDelivery() {
  testing.value = true;
  actionMsg.value = null;
  try {
    const { data } = await nexceoReportsAPI.testDailyDelivery();
    actionMsg.value = {
      success: data.success,
      text: data.message || 'WhatsApp report sent.',
    };
    await loadSnapshots();
  } catch (e) {
    actionMsg.value = {
      success: false,
      text: e?.response?.data?.error || 'Send failed.',
    };
  } finally {
    testing.value = false;
    clearActionMsg();
  }
}

function applyFilters() {
  loadMetrics();
}

onMounted(() => {
  loadMetrics();
  loadSnapshots();
  loadProjects();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 h-14 border-b border-n-weak bg-n-background shrink-0"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-bar-chart-2 size-4 text-woot-500" />
        <h1 class="text-base font-semibold text-n-slate-12">NEXCEO Reports</h1>
      </div>
      <div class="flex items-center gap-2">
        <button
          :disabled="generating"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors"
          @click="generateReport"
        >
          <span
            v-if="generating"
            class="i-lucide-loader-2 size-3.5 animate-spin"
          />
          <span v-else class="i-lucide-file-text size-3.5" />
          Generate Report Now
        </button>
        <button
          :disabled="testing"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
          @click="testDelivery"
        >
          <span
            v-if="testing"
            class="i-lucide-loader-2 size-3.5 animate-spin"
          />
          <span v-else class="i-lucide-send size-3.5" />
          Send WhatsApp Report
        </button>
      </div>
    </div>

    <!-- Action message -->
    <div
      v-if="actionMsg"
      class="mx-4 mt-3 text-xs px-3 py-2 rounded-lg"
      :class="
        actionMsg.success
          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
          : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
      "
    >
      {{ actionMsg.text }}
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <div class="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <!-- ── Filters ── -->
        <div class="flex flex-wrap items-center gap-3">
          <select
            v-model="filters.range"
            class="text-sm px-3 py-1.5 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
            @change="applyFilters"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 days</option>
            <option value="last30">Last 30 days</option>
            <option value="custom">Custom</option>
          </select>

          <template v-if="filters.range === 'custom'">
            <input
              v-model="filters.start_date"
              type="date"
              class="text-sm px-3 py-1.5 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
            />
            <span class="text-xs text-n-slate-7">to</span>
            <input
              v-model="filters.end_date"
              type="date"
              class="text-sm px-3 py-1.5 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
            />
          </template>

          <select
            v-model="filters.project_id"
            class="text-sm px-3 py-1.5 border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
            @change="applyFilters"
          >
            <option value="">All projects</option>
            <option v-for="p in projects" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>

          <button
            class="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
            @click="applyFilters"
          >
            <span class="i-lucide-refresh-cw size-3.5" />
            Apply
          </button>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="flex items-center justify-center py-16">
          <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
        </div>

        <!-- Error -->
        <div
          v-else-if="loadError"
          class="text-sm text-red-600 py-8 text-center"
        >
          {{ loadError }}
        </div>

        <template v-else-if="metrics">
          <!-- ── Overview Cards ── -->
          <section>
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3"
            >
              Overview
            </h2>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div
                v-for="card in [
                  {
                    label: 'New Leads',
                    value: ov.total_new_leads || 0,
                    icon: 'i-lucide-users',
                    color: 'text-woot-500',
                  },
                  {
                    label: 'Verified Leads',
                    value: ov.verified_leads || 0,
                    icon: 'i-lucide-badge-check',
                    color: 'text-teal-600',
                  },
                  {
                    label: 'Hot Leads',
                    value: ov.hot_leads || 0,
                    icon: 'i-lucide-flame',
                    color: 'text-orange-500',
                  },
                  {
                    label: 'Site Visits',
                    value: ov.site_visits_scheduled || 0,
                    icon: 'i-lucide-calendar-check',
                    color: 'text-green-600',
                  },
                  {
                    label: 'Video Calls',
                    value: ov.video_calls_scheduled || 0,
                    icon: 'i-lucide-video',
                    color: 'text-woot-500',
                  },
                  {
                    label: 'Bookings',
                    value: ov.booked_leads || 0,
                    icon: 'i-lucide-check-circle',
                    color: 'text-emerald-600',
                  },
                  {
                    label: 'Handoffs',
                    value: ov.human_handoffs || 0,
                    icon: 'i-lucide-user-check',
                    color: 'text-blue-500',
                  },
                  {
                    label: 'Pending Approvals',
                    value: ov.pending_approvals || 0,
                    icon: 'i-lucide-clock',
                    color: 'text-yellow-600',
                  },
                  {
                    label: 'AI Replies',
                    value: aiMetrics.ai_replies_sent || 0,
                    icon: 'i-lucide-bot',
                    color: 'text-purple-500',
                  },
                  {
                    label: 'Notif. Failures',
                    value: ndMetrics.failed || 0,
                    icon: 'i-lucide-alert-circle',
                    color: 'text-red-500',
                  },
                ]"
                :key="card.label"
                class="bg-n-background border border-n-weak rounded-xl p-4 flex flex-col gap-1"
              >
                <div class="flex items-center gap-1.5">
                  <span class="size-3.5" :class="[card.icon, card.color]" />
                  <span class="text-xs text-n-slate-8">{{ card.label }}</span>
                </div>
                <div class="text-2xl font-bold text-n-slate-12">
                  {{ card.value }}
                </div>
              </div>
            </div>
          </section>

          <!-- ── Project Table ── -->
          <section
            v-if="projectMetrics.length > 0"
            class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
          >
            <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Project Performance
              </h2>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-n-weak bg-n-alpha-1">
                    <th
                      class="text-left px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Project
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      New
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Verified
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Hot
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Visits
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Video
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Booked
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Lost
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Conv%
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Pending
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="p in projectMetrics"
                    :key="p.project_id"
                    class="border-b border-n-weak/50 last:border-0 hover:bg-n-alpha-1 transition-colors"
                  >
                    <td class="px-4 py-2.5 font-medium text-n-slate-12">
                      {{ p.project_name }}
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ p.new_leads || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right">
                      <span
                        v-if="p.verified_leads > 0"
                        class="text-teal-600 font-semibold"
                        >{{ p.verified_leads }}</span
                      >
                      <span v-else class="text-n-slate-8">0</span>
                    </td>
                    <td class="px-4 py-2.5 text-right">
                      <span
                        v-if="p.hot_leads > 0"
                        class="text-orange-600 font-semibold"
                        >{{ p.hot_leads }}</span
                      >
                      <span v-else class="text-n-slate-8">0</span>
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ p.site_visits_scheduled || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ p.video_calls || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right">
                      <span
                        v-if="p.booked > 0"
                        class="text-emerald-600 font-semibold"
                        >{{ p.booked }}</span
                      >
                      <span v-else class="text-n-slate-8">0</span>
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-8">
                      {{ p.lost || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right">
                      <span
                        v-if="p.conversion_rate != null"
                        class="text-xs font-medium px-1.5 py-0.5 rounded-full"
                        :class="
                          p.conversion_rate > 10
                            ? 'bg-green-50 text-green-700'
                            : 'bg-n-alpha-2 text-n-slate-9'
                        "
                        >{{ p.conversion_rate }}%</span
                      >
                      <span v-else class="text-n-slate-7">—</span>
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-8">
                      {{ p.pending_approvals || 0 }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ── Source Breakdown + Funnel ── -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Source breakdown -->
            <section
              class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
            >
              <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Lead Sources
                </h2>
              </div>
              <div class="px-5 py-4 space-y-2">
                <div
                  v-for="[ch, cnt] in [
                    ['WhatsApp', sourceMetrics.whatsapp || 0],
                    ['Website', sourceMetrics.website || 0],
                    ['Instagram', sourceMetrics.instagram || 0],
                    ['Facebook', sourceMetrics.facebook || 0],
                    ['Email', sourceMetrics.email || 0],
                    ['Manual', sourceMetrics.manual || 0],
                    ['Unknown', sourceMetrics.unknown || 0],
                  ]"
                  :key="ch"
                  class="flex items-center justify-between"
                >
                  <span class="text-sm text-n-slate-11">{{ ch }}</span>
                  <span class="text-sm font-semibold text-n-slate-12">{{
                    cnt
                  }}</span>
                </div>
              </div>
            </section>

            <!-- Funnel -->
            <section
              class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
            >
              <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Funnel (Current)
                </h2>
              </div>
              <div class="px-5 py-4 space-y-2">
                <div
                  v-for="[stage, count] in Object.entries(funnelMetrics)"
                  :key="stage"
                  class="flex items-center gap-2"
                >
                  <span class="text-xs text-n-slate-9 w-36 truncate">{{
                    stage.replace(/_/g, ' ')
                  }}</span>
                  <div
                    class="flex-1 h-2 bg-n-alpha-2 rounded-full overflow-hidden"
                  >
                    <div
                      class="h-full bg-woot-500 rounded-full"
                      :style="`width:${funnelPct(count)}%`"
                    />
                  </div>
                  <span
                    class="text-xs font-semibold text-n-slate-12 w-6 text-right"
                    >{{ count }}</span
                  >
                </div>
                <p v-if="funnelTotal === 0" class="text-xs text-n-slate-7">
                  No active leads in funnel.
                </p>
              </div>
            </section>
          </div>

          <!-- ── Agent Performance ── -->
          <section
            class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
          >
            <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Agent Performance
              </h2>
            </div>
            <div
              v-if="agentMetrics.length === 0"
              class="px-5 py-6 text-sm text-n-slate-7 text-center"
            >
              No active project team members found.
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-n-weak">
                    <th
                      class="text-left px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Agent
                    </th>
                    <th
                      class="text-left px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Projects
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Leads
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Verified
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Hot
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Visits
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Video
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Bookings
                    </th>
                    <th
                      class="text-right px-4 py-2 text-xs font-semibold text-n-slate-9"
                    >
                      Lost
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="a in agentMetrics"
                    :key="a.user_id"
                    class="border-b border-n-weak/50 last:border-0 hover:bg-n-alpha-1 transition-colors"
                  >
                    <td class="px-4 py-2.5 font-medium text-n-slate-12">
                      {{ a.name }}
                    </td>
                    <td
                      class="px-4 py-2.5 text-xs text-n-slate-9 max-w-[140px] truncate"
                      :title="a.projects"
                    >
                      {{ a.projects || '—' }}
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ a.assigned_leads || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right">
                      <span
                        v-if="a.verified_leads > 0"
                        class="text-teal-600 font-semibold"
                        >{{ a.verified_leads }}</span
                      >
                      <span v-else class="text-n-slate-8">0</span>
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ a.hot_leads || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ a.site_visits || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ a.video_calls || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-12">
                      {{ a.bookings || 0 }}
                    </td>
                    <td class="px-4 py-2.5 text-right text-n-slate-8">
                      {{ a.lost || 0 }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ── AI + Notification Health ── -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <section
              class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
            >
              <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  AI Health
                </h2>
              </div>
              <!-- When the ai_logs table is empty, show a note rather than 0/0 -->
              <div
                v-if="!aiTrackingAvailable"
                class="px-5 py-6 text-sm text-n-slate-7 text-center"
              >
                <span
                  class="i-lucide-info size-4 text-n-slate-6 inline-block mb-1"
                />
                <p>
                  Not tracked yet — AI reply logs will appear here once the AI
                  starts processing messages.
                </p>
              </div>
              <div v-else class="px-5 py-4 space-y-2">
                <div
                  v-for="[label, val] in [
                    ['Replies sent', aiMetrics.ai_replies_sent || 0],
                    ['Errors', aiMetrics.ai_errors || 0],
                    ['Handoffs', aiMetrics.ai_handoffs || 0],
                    ['Low confidence', aiMetrics.ai_confidence_low || 0],
                    [
                      'Tokens (in/out)',
                      `${aiMetrics.tokens_input || 0} / ${aiMetrics.tokens_output || 0}`,
                    ],
                    [
                      'Est. cost (USD)',
                      `$${(aiMetrics.estimated_cost_usd || 0).toFixed(4)}`,
                    ],
                  ]"
                  :key="label"
                  class="flex items-center justify-between"
                >
                  <span class="text-sm text-n-slate-9">{{ label }}</span>
                  <span class="text-sm font-semibold text-n-slate-12">{{
                    val
                  }}</span>
                </div>
              </div>
            </section>

            <section
              class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
            >
              <div class="px-5 py-3 border-b border-n-weak bg-n-alpha-1">
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Notification Delivery
                </h2>
              </div>
              <div class="px-5 py-4 space-y-2">
                <div
                  v-for="[label, val, color] in [
                    ['Total', ndMetrics.total || 0, ''],
                    ['Delivered', ndMetrics.delivered || 0, 'text-green-600'],
                    [
                      'Failed',
                      ndMetrics.failed || 0,
                      ndMetrics.failed > 0 ? 'text-red-600' : '',
                    ],
                    ['Skipped', ndMetrics.skipped || 0, ''],
                    [
                      'WhatsApp delivered',
                      ndMetrics.whatsapp_delivered || 0,
                      'text-green-600',
                    ],
                    [
                      'Email delivered',
                      ndMetrics.email_delivered || 0,
                      'text-green-600',
                    ],
                    [
                      'Private notes sent',
                      ndMetrics.private_notes_sent || 0,
                      'text-blue-600',
                    ],
                  ]"
                  :key="label"
                  class="flex items-center justify-between"
                >
                  <span class="text-sm text-n-slate-9">{{ label }}</span>
                  <span
                    class="text-sm font-semibold"
                    :class="color || 'text-n-slate-12'"
                    >{{ val }}</span
                  >
                </div>
              </div>
            </section>
          </div>
        </template>

        <!-- Empty state when no metrics -->
        <div
          v-else
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <span class="i-lucide-bar-chart-2 size-10 text-n-slate-6 mb-3" />
          <p class="text-sm text-n-slate-8">
            No metrics loaded. Apply filters to get started.
          </p>
        </div>

        <!-- ── Report Snapshots ── -->
        <section
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center justify-between"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Daily Report History
            </h2>
          </div>
          <div
            v-if="snapshots.length === 0"
            class="px-5 py-6 text-sm text-n-slate-7 text-center"
          >
            No report snapshots yet. Click "Generate Report Now" to create one.
          </div>
          <div v-else class="divide-y divide-n-weak/50">
            <div
              v-for="snap in snapshots"
              :key="snap.id"
              class="px-5 py-3 flex items-center justify-between gap-3 hover:bg-n-alpha-1 transition-colors"
            >
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">{{
                  snap.date_label
                }}</span>
                <span class="text-xs text-n-slate-7">
                  Generated:
                  {{
                    snap.generated_at
                      ? new Date(snap.generated_at).toLocaleString()
                      : '—'
                  }}
                  <span v-if="snap.delivered_at">
                    · Delivered:
                    {{ new Date(snap.delivered_at).toLocaleString() }}</span
                  >
                </span>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span
                  class="text-xs px-2 py-0.5 rounded-full font-medium"
                  :class="{
                    'bg-green-50 text-green-700': snap.status === 'delivered',
                    'bg-blue-50 text-blue-700': snap.status === 'generated',
                    'bg-red-50 text-red-700': snap.status === 'failed',
                    'bg-n-alpha-2 text-n-slate-9':
                      snap.status === 'pending' || snap.status === 'skipped',
                  }"
                  >{{ snap.status }}</span
                >
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
